import { useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

// fix_issue3.md #5 (owner policies) + #6 (configurable specification sheet) + v4_fix_issues #1/#3
// (minimum up-front payment). Shared by the list/edit-a-listing form. `form` must carry
// `specifications: [{label,value}]` and `policies: {securityDeposit,cashOnDelivery,freeShipping,
// freeMaintenance,minBookingAmount,minBookingPercent}`.
export const COD_MAX_DAY_RATE = 500; // mirrors rentalapi pricingConfig.codMaxDayRate
export const emptyPolicies = {
  securityDeposit: false, cashOnDelivery: false, freeShipping: false, freeMaintenance: false,
  minBookingAmount: 0, minBookingPercent: 0,
  // v5_fix_issues #5/#10 - car_rental only (ignored elsewhere).
  includedKmPerDay: 0, lateReturnUnit: 'full_day'
};

// Strip anything COD-invalid before sending (the box may have been ticked before the price rose),
// and make sure only ONE of the two minimum-payment fields is ever non-zero.
export function sanitizeListingExtras(form) {
  const price = Number(form.price);
  const p = { ...emptyPolicies, ...(form.policies || {}) };
  const minAmount = Number(p.minBookingAmount) || 0;
  const minPercent = Number(p.minBookingPercent) || 0;
  return {
    specifications: (form.specifications || []).filter((s) => s.label || s.value),
    policies: {
      securityDeposit: !!p.securityDeposit,
      cashOnDelivery: !!p.cashOnDelivery && price > 0 && price <= COD_MAX_DAY_RATE,
      freeShipping: !!p.freeShipping,
      freeMaintenance: !!p.freeMaintenance,
      minBookingAmount: minAmount > 0 ? minAmount : 0,
      minBookingPercent: minAmount > 0 ? 0 : (minPercent > 0 ? minPercent : 0),
      includedKmPerDay: Math.max(0, Number(p.includedKmPerDay) || 0),
      lateReturnUnit: p.lateReturnUnit === 'half_day' ? 'half_day' : 'full_day'
    }
  };
}

// v6 stepper forms - `section` splits this sub-form across wizard steps:
//   'specs'    -> just the free-form specification rows (listing wizard step 3)
//   'policies' -> rental-policy flags + minimum up-front payment + car km/late-return (step 2)
//   'all'      -> everything, in one block (default; back-compat with the pre-wizard single form)
export default function ListingExtraFields({ form, setForm, vertical, section = 'all' }) {
  const showSpecs = section === 'all' || section === 'specs';
  const showPolicies = section === 'all' || section === 'policies';
  const specs = form.specifications || [];
  const policies = { ...emptyPolicies, ...(form.policies || {}) };
  const codAllowed = Number(form.price) > 0 && Number(form.price) <= COD_MAX_DAY_RATE;
  const isCar = vertical === 'car_rental';
  const [kmLimitOn, setKmLimitOn] = useState(Number(policies.includedKmPerDay) > 0);

  const setPolicy = (key, value) => setForm((prev) => ({ ...prev, policies: { ...emptyPolicies, ...(prev.policies || {}), [key]: value } }));

  const [minMode, setMinMode] = useState(
    Number(policies.minBookingAmount) > 0 ? 'amount'
      : (Number(policies.minBookingPercent) > 0 ? 'percent' : 'none')
  );

  const setSpec = (index, key) => (e) => setForm((prev) => ({
    ...prev,
    specifications: (prev.specifications || []).map((s, i) => (i === index ? { ...s, [key]: e.target.value } : s))
  }));
  const addSpec = () => setForm((prev) => ({ ...prev, specifications: [...(prev.specifications || []), { label: '', value: '' }] }));
  const removeSpec = (index) => setForm((prev) => ({ ...prev, specifications: (prev.specifications || []).filter((_, i) => i !== index) }));
  const togglePolicy = (key) => (e) => setForm((prev) => ({ ...prev, policies: { ...emptyPolicies, ...(prev.policies || {}), [key]: e.target.checked } }));

  const chooseMinMode = (mode) => {
    setMinMode(mode);
    setForm((prev) => ({ ...prev, policies: { ...emptyPolicies, ...(prev.policies || {}), minBookingAmount: 0, minBookingPercent: 0 } }));
  };
  const setMinValue = (key) => (e) => setForm((prev) => ({
    ...prev,
    policies: {
      ...emptyPolicies, ...(prev.policies || {}),
      minBookingAmount: key === 'minBookingAmount' ? Number(e.target.value) || 0 : 0,
      minBookingPercent: key === 'minBookingPercent' ? Number(e.target.value) || 0 : 0
    }
  }));

  return (
    <>
      {showSpecs && (
      <Form.Group className="mb-3">
        <Form.Label>Specifications</Form.Label>
        {specs.map((spec, index) => (
          <Row key={index} className="g-2 mb-2">
            <Col xs={5}><Form.Control size="sm" placeholder="Label" value={spec.label} onChange={setSpec(index, 'label')} /></Col>
            <Col xs={5}><Form.Control size="sm" placeholder="Value" value={spec.value} onChange={setSpec(index, 'value')} /></Col>
            <Col xs={2}><Button size="sm" variant="outline-danger" onClick={() => removeSpec(index)} aria-label={`Remove specification ${index + 1}`}>✕</Button></Col>
          </Row>
        ))}
        <Button size="sm" variant="outline-secondary" onClick={addSpec}>+ Add specification</Button>
      </Form.Group>
      )}

      {showPolicies && (
      <>
      <Form.Group className="mb-3">
        <Form.Label>Rental policies</Form.Label>
        <Form.Check type="checkbox" id="policy-securityDeposit"
          label="Require a refundable security deposit (set as a platform-wide % of the rental)"
          checked={policies.securityDeposit} onChange={togglePolicy('securityDeposit')} />
        <Form.Check type="checkbox" id="policy-cashOnDelivery"
          label={`Cash on delivery — you collect cash at handover${codAllowed ? '' : ` (only for items ≤ ₹${COD_MAX_DAY_RATE}/day)`}`}
          checked={policies.cashOnDelivery && codAllowed} disabled={!codAllowed} onChange={togglePolicy('cashOnDelivery')} />
        <Form.Check type="checkbox" id="policy-freeShipping" label="Free shipping — you arrange shipping (platform still tracks the handover)"
          checked={policies.freeShipping} onChange={togglePolicy('freeShipping')} />
        <Form.Check type="checkbox" id="policy-freeMaintenance" label="Free maintenance — you service the item"
          checked={policies.freeMaintenance} onChange={togglePolicy('freeMaintenance')} />
        {policies.cashOnDelivery && codAllowed && (
          <Form.Text className="text-muted d-block">
            If the customer cancels at the last moment you remain responsible for the extra delivery cost, the
            platform fee, and 50% of the commission on the whole booking.
          </Form.Text>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Minimum up-front payment</Form.Label>
        <Form.Text className="text-muted d-block mb-2">
          The least a customer can pay when booking — the rest stays due. The full amount is always owed;
          this doesn&apos;t discount anything.
        </Form.Text>
        <Form.Check type="radio" id="min-none" name="minMode" label="No minimum — customer may pay any amount or the full total"
          checked={minMode === 'none'} onChange={() => chooseMinMode('none')} />
        <Form.Check type="radio" id="min-amount" name="minMode" label="A fixed ₹ amount" className="mt-1"
          checked={minMode === 'amount'} onChange={() => chooseMinMode('amount')} />
        {minMode === 'amount' && (
          <Form.Control type="number" min={0} size="sm" className="mt-1" style={{ maxWidth: 200 }}
            placeholder="₹" value={policies.minBookingAmount || ''} onChange={setMinValue('minBookingAmount')} />
        )}
        <Form.Check type="radio" id="min-percent" name="minMode" label="A percentage of the booking total" className="mt-1"
          checked={minMode === 'percent'} onChange={() => chooseMinMode('percent')} />
        {minMode === 'percent' && (
          <Form.Control type="number" min={0} max={100} size="sm" className="mt-1" style={{ maxWidth: 200 }}
            placeholder="%" value={policies.minBookingPercent || ''} onChange={setMinValue('minBookingPercent')} />
        )}
      </Form.Group>

      {isCar && (
        <>
          {/* v5_fix_issues #5 - optional daily km limit for a per-day rental. */}
          <Form.Group className="mb-3">
            <Form.Label>Daily kilometre limit</Form.Label>
            <Form.Check
              type="checkbox" id="policy-kmLimit"
              label="Include a daily km limit (extra km billed at the ₹/km rate above)"
              checked={kmLimitOn}
              onChange={(e) => {
                setKmLimitOn(e.target.checked);
                setPolicy('includedKmPerDay', e.target.checked ? (Number(policies.includedKmPerDay) || 300) : 0);
              }}
            />
            {kmLimitOn && (
              <Form.Control
                type="number" min={0} size="sm" className="mt-1" style={{ maxWidth: 200 }}
                value={policies.includedKmPerDay || ''} placeholder="300"
                onChange={(e) => setPolicy('includedKmPerDay', Number(e.target.value) || 0)}
              />
            )}
          </Form.Group>

          {/* v5_fix_issues #10 - what a return more than 1 hour past midnight of the end date costs. */}
          <Form.Group className="mb-3">
            <Form.Label>Late-return charge</Form.Label>
            <Form.Text className="text-muted d-block mb-1">
              Returned before midnight of the end date: no charge. Within the next hour: one extra
              hour. Later: the charge below, at the daily rate.
            </Form.Text>
            <Form.Check type="radio" name="lateReturnUnit" id="lru-full" label="A full day"
              checked={(policies.lateReturnUnit || 'full_day') === 'full_day'}
              onChange={() => setPolicy('lateReturnUnit', 'full_day')} />
            <Form.Check type="radio" name="lateReturnUnit" id="lru-half" label="A half day" className="mt-1"
              checked={policies.lateReturnUnit === 'half_day'}
              onChange={() => setPolicy('lateReturnUnit', 'half_day')} />
          </Form.Group>
        </>
      )}
      </>
      )}
    </>
  );
}
